import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, resolveAvatar } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Teachers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "coach123", role: "teacher" });
  const { data, isLoading } = useQuery({ queryKey: ["teachers"], queryFn: async () => (await api.get("/users?role=teacher")).data });

  const create = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      await api.post("/users", form);
      toast.success(`Teacher created! Password: ${form.password}`); setOpen(false); setForm({ name: "", email: "", password: "coach123", role: "teacher" });
      qc.invalidateQueries({ queryKey: ["teachers"] });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (t) => { await api.put(`/users/${t.id}`, { active: !t.active }); qc.invalidateQueries({ queryKey: ["teachers"] }); };
  const del = async (t) => { if (!window.confirm(`Remove coach ${t.name}?`)) return; await api.delete(`/users/${t.id}`); toast.success("Teacher removed"); qc.invalidateQueries({ queryKey: ["teachers"] }); };
  const resetPw = async (t) => { const r = await api.post(`/users/${t.id}/reset-password`); toast.success(`Password reset for ${t.name}: ${r.data.new_password}`); };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="teachers-page">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-3xl text-csh-blue">Teachers 🎓</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full gradient-purple text-white" data-testid="add-teacher-btn"><Icons.Plus size={18} /> New Coach</Button></DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">New Coach</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-2 py-6" data-testid="teacher-name" />
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-2 py-6" data-testid="teacher-email" />
              <Input placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl border-2 py-6" />
              <Button onClick={create} className="w-full rounded-full gradient-orange py-6 font-bold text-white" data-testid="save-teacher-btn">Create Coach</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {(!data || data.length === 0) ? <EmptyState icon="GraduationCap" title="No teachers" /> : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm border border-black/5" data-testid={`teacher-${t.id}`}>
              <img src={resolveAvatar(t.avatar)} alt="" className="h-14 w-14 rounded-2xl object-cover" />
              <div className="flex-1">
                <h3 className="font-head text-lg text-csh-blue">{t.name}</h3>
                <p className="text-xs text-muted-foreground">{t.email}</p>
              </div>
              <button data-testid={`reset-teacher-pw-${t.id}`} onClick={() => resetPw(t)} title="Reset password" className="rounded-lg p-2 hover:bg-black/5"><Icons.KeyRound size={16} className="text-csh-purple" /></button>
              <button onClick={() => toggle(t)} className={`rounded-full px-3 py-1 text-xs font-bold ${t.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {t.active ? "Active" : "Inactive"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
